/**
 * PIN do operador (proposta §3, operador de caixa): 4 a 6 algarismos para
 * trocar de operador em 2 segundos, mesmo sem rede.
 *
 * PBKDF2-SHA256 com sal próprio, por WebCrypto: a mesma função na nuvem
 * (que grava) e no terminal (que verifica offline). Um PIN curto adivinha-se
 * por força bruta a partir do hash; o hash só evita guardá-lo em claro. A
 * defesa real é o terminal: tentativas limitadas e bloqueio (`PinGuard`).
 */
export const PIN_RE = /^\d{4,6}$/;
export const PIN_ITERATIONS = 120_000;
const b64 = (u) => { let s = ''; for (const b of u)
    s += String.fromCharCode(b); return btoa(s); };
const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
export async function hashPin(pin, saltB64, iterations = PIN_ITERATIONS) {
    if (!PIN_RE.test(pin))
        throw new RangeError('O PIN tem de ter 4 a 6 algarismos');
    const salt = saltB64 ? unb64(saltB64) : globalThis.crypto.getRandomValues(new Uint8Array(16));
    const key = await globalThis.crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, ['deriveBits']);
    const bits = await globalThis.crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations }, key, 256);
    return { salt: b64(salt), hash: b64(new Uint8Array(bits)), iterations };
}
export async function verifyPin(pin, stored) {
    if (!PIN_RE.test(pin))
        return false;
    const { hash } = await hashPin(pin, stored.salt, stored.iterations);
    // Comparação em tempo constante (o tamanho é sempre o mesmo).
    let diff = hash.length ^ stored.hash.length;
    for (let i = 0; i < Math.min(hash.length, stored.hash.length); i++)
        diff |= hash.charCodeAt(i) ^ stored.hash.charCodeAt(i);
    return diff === 0;
}
export const PIN_MAX_FAILURES = 5;
export function pinGuardCheck(s, now) {
    if (!s || s.lockedUntil <= now)
        return { allowed: true, waitMs: 0 };
    return { allowed: false, waitMs: s.lockedUntil - now };
}
export function pinGuardRecord(s, ok, now) {
    const cur = s ?? { failures: 0, lockedUntil: 0, lockouts: 0 };
    if (ok)
        return { failures: 0, lockedUntil: 0, lockouts: cur.lockouts };
    const failures = cur.failures + 1;
    if (failures < PIN_MAX_FAILURES)
        return { ...cur, failures };
    const lockouts = cur.lockouts + 1;
    return { failures: 0, lockouts, lockedUntil: now + Math.min(5 * 60_000 * 2 ** (lockouts - 1), 3_600_000) };
}
//# sourceMappingURL=pin.js.map